import React, { useState, useContext } from 'react';
import { ethers } from 'ethers';
import { AuthContext } from '../../AuthContext';

// ERC20 ABI for token operations
const ERC20_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function decimals() external view returns (uint8)",
  "function symbol() external view returns (string)",
  "function balanceOf(address account) external view returns (uint256)"
];

// Token configuration matching Web3Pay.js structure
const SUPPORTED_TOKENS = {
  USD1: 'USD1 (Gold/Reserve Backed)',
  USDT: 'USDT (Reserves Backed)',
  USDC: 'USDC (Government-Reg Backed)',
  DAI: 'DAI (Collateral Backed)',
  FDUSD: 'FDUSD (Reserves Backed)',
  EURT: 'EURT (Euro Reserves Backed)',
  WETH: 'WETH (Wrapped Ethereum)'
};

const BankCurrent = ({
  walletAddress,
  web3payContract,
  contractMode,
  contractData,
  balances,
  refreshBalances,
  contractTransactions
}) => {
  const { isAuthenticated } = useContext(AuthContext);

  // Component state
  const [selectedToken, setSelectedToken] = useState('USD1');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferRecipient, setTransferRecipient] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionMode, setActionMode] = useState('');

  // Get transaction data based on mode (matching Web3Pay.js pattern)
  const getDisplayTransactions = () => {
    if (contractMode && contractTransactions?.length > 0) {
      return contractTransactions.slice(0, 5).map(tx => ({
        date: tx.date || new Date(tx.timestamp * 1000).toLocaleDateString(),
        desc: tx.description || `${tx.metadata?.paymentType || 'Transaction'} (Contract)`,
        amount: `${parseFloat(tx.amount) >= 0 ? '+' : ''}${parseFloat(tx.amount || 0).toFixed(6)} ${tx.tokenSymbol || 'TOKEN'}`,
        type: parseFloat(tx.amount || 0) >= 0 ? 'credit' : 'debit'
      }));
    }

    // Mock transactions for non-contract mode
    return [
      { date: '2025-09-14', desc: 'Deposit from Farm Sale', amount: '+0.5 USD1', type: 'credit' },
      { date: '2025-09-13', desc: 'P2P to Alice', amount: '-0.1 USDC', type: 'debit' },
      { date: '2025-09-12', desc: 'Merchant Payment', amount: '-0.05 USDT', type: 'debit' },
      { date: '2025-09-11', desc: 'Staking Rewards', amount: '+0.02 DAI', type: 'credit' }
    ];
  };

  // Get balance for selected token (matching Web3Pay.js pattern)
  const getTokenBalance = (tokenSymbol) => {
    if (contractMode && balances) {
      return parseFloat(balances[tokenSymbol] || '0');
    }
    // Mock balances for API mode
    const mockBalances = {
      USD1: 1.25, USDC: 2.50, USDT: 0.75, DAI: 0.85,
      FDUSD: 0.45, EURT: 0.35, WETH: 0.15, ETH: 0.25
    };
    return mockBalances[tokenSymbol] || 0;
  };

  // Get token decimals dynamically from contract (matching BankPay.js pattern)
  const getTokenDecimals = async (tokenAddress) => {
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      return await tokenContract.decimals();
    } catch (error) {
      console.warn(`Failed to get decimals for token ${tokenAddress}:`, error);
      return 18; // Default fallback
    }
  };

  // Enhanced ERC20 deposit flow with approve → wait → deposit (matching BankPay.js error handling)
  const depositToken = async () => {
    if (!web3payContract || !contractData?.tokenAddresses || !contractMode) {
      setError('Web3Pay contract not available. Please check your connection.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const tokenAddress = contractData.tokenAddresses[selectedToken];
      if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error(`Token ${selectedToken} is not configured in the contract`);
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();

      // Get token decimals dynamically
      const decimals = await getTokenDecimals(tokenAddress);
      const amountInWei = ethers.parseUnits(transferAmount, decimals);

      // Step 1: Check current allowance
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
      const currentAllowance = await tokenContract.allowance(walletAddress, web3payContract.target);

      // Step 2: Approve if necessary
      if (currentAllowance < amountInWei) {
        const approveTx = await tokenContract.approve(web3payContract.target, amountInWei);
        await approveTx.wait();
      }

      // Step 3: Deposit to contract
      const depositTx = await web3payContract.depositERC20(tokenAddress, amountInWei);
      const depositReceipt = await depositTx.wait();

      // Refresh balances
      if (refreshBalances) {
        await refreshBalances();
      }

      // Reset form
      setTransferAmount('');
      setActionMode('');

      console.log(`Successfully deposited ${transferAmount} ${selectedToken}! TX: ${depositReceipt.hash}`);

    } catch (error) {
      console.error('Deposit error:', error);
      setError(`Deposit failed: ${error.message || error.reason || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Withdraw ERC20 token from contract (matching BankPay.js pattern)
  const withdrawToken = async () => {
    if (!web3payContract || !contractData?.tokenAddresses || !contractMode) {
      setError('Web3Pay contract not available. Please check your connection.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const tokenAddress = contractData.tokenAddresses[selectedToken];
      if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error(`Token ${selectedToken} is not configured in the contract`);
      }

      // Get token decimals dynamically
      const decimals = await getTokenDecimals(tokenAddress);
      const amountInWei = ethers.parseUnits(transferAmount, decimals);

      const tx = await web3payContract.withdrawERC20(tokenAddress, amountInWei);
      const receipt = await tx.wait();

      // Refresh balances
      if (refreshBalances) {
        await refreshBalances();
      }

      // Reset form
      setTransferAmount('');
      setActionMode('');

      console.log(`Successfully withdrew ${transferAmount} ${selectedToken}! TX: ${receipt.hash}`);

    } catch (error) {
      console.error('Withdraw error:', error);
      setError(`Withdraw failed: ${error.message || error.reason || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Transfer tokens via contract (matching BankPay.js pattern)
  const transferToken = async () => {
    if (!web3payContract || !contractData?.tokenAddresses || !contractMode) {
      setError('Web3Pay contract not available. Please check your connection.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const tokenAddress = contractData.tokenAddresses[selectedToken];
      if (!tokenAddress || tokenAddress === '0x0000000000000000000000000000000000000000') {
        throw new Error(`Token ${selectedToken} is not configured in the contract`);
      }

      // Validate recipient address
      if (!ethers.isAddress(transferRecipient) && !transferRecipient.endsWith('.eth')) {
        throw new Error('Please enter a valid Ethereum address or ENS name');
      }

      // Get token decimals dynamically
      const decimals = await getTokenDecimals(tokenAddress);
      const amountInWei = ethers.parseUnits(transferAmount, decimals);

      const tx = await web3payContract.transfer(
        transferRecipient,
        tokenAddress,
        amountInWei,
        `Transfer ${transferAmount} ${selectedToken}`
      );
      const receipt = await tx.wait();

      // Refresh balances
      if (refreshBalances) {
        await refreshBalances();
      }

      // Reset form
      setTransferAmount('');
      setTransferRecipient('');
      setActionMode('');

      console.log(`Successfully transferred ${transferAmount} ${selectedToken} to ${transferRecipient}! TX: ${receipt.hash}`);

    } catch (error) {
      console.error('Transfer error:', error);
      setError(`Transfer failed: ${error.message || error.reason || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle action based on mode
  const handleAction = () => {
    switch (actionMode) {
      case 'deposit':
        return depositToken();
      case 'withdraw':
        return withdrawToken();
      case 'transfer':
        return transferToken();
      default:
        return;
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        background: '#111',
        border: '1px solid #ffd700',
        borderRadius: '8px',
        padding: '30px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#ffd700', marginBottom: '20px' }}>
          Bank Current - Authentication Required
        </h2>
        <p style={{ color: '#ccc', marginBottom: '30px' }}>
          Please connect your wallet to access the banking system.
        </p>
      </div>
    );
  }

  const currentBalance = getTokenBalance(selectedToken);
  const transactions = getDisplayTransactions();

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#FFD700', marginBottom: '5px' }}>
            Bank Current Account
          </h2>
          <p style={{ color: '#ccc' }}>
            {contractMode
              ? 'Manage your on-chain token balances with smart contract integration on Sepolia Testnet.'
              : 'Manage your stablecoin balance with low fees.'
            }
          </p>
        </div>
        <div style={{
          background: contractMode ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 215, 0, 0.1)',
          border: contractMode ? '1px solid #00ff00' : '1px solid #ffd700',
          borderRadius: '6px',
          padding: '5px 10px',
          fontSize: '12px',
          color: contractMode ? '#00ff00' : '#ffd700'
        }}>
          {contractMode ? 'Contract Mode' : 'API Mode'}
        </div>
      </div>

      {walletAddress && (
        <div style={{
          background: 'rgba(0, 255, 0, 0.1)',
          border: '1px solid #00ff00',
          borderRadius: '6px',
          padding: '10px',
          marginBottom: '20px',
          display: 'inline-block'
        }}>
          <span style={{ color: '#00ff00' }}>Connected: </span>
          <span style={{ color: '#ffd700' }}>
            {walletAddress.substring(0, 6)}...{walletAddress.substring(walletAddress.length - 4)}
          </span>
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid #ff4444',
          color: '#ff6666',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '30px',
        marginBottom: '30px'
      }}>
        {/* Balance & Token Selector */}
        <div style={{
          background: '#111',
          border: '1px solid #FFD700',
          padding: '20px',
          borderRadius: '8px'
        }}>
          <h3 style={{ color: '#FFD700', marginBottom: '15px' }}>Token Balance</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', color: '#FFD700', marginBottom: '5px' }}>
              Select Token
            </label>
            <select
              value={selectedToken}
              onChange={(e) => setSelectedToken(e.target.value)}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ffd700',
                borderRadius: '8px',
                background: loading ? '#222' : '#1a1a2e',
                color: loading ? '#666' : '#ffd700',
                fontSize: '14px'
              }}
            >
              {Object.entries(SUPPORTED_TOKENS).map(([key, label]) => (
                <option key={key} value={key} style={{ background: '#1a1a2e', color: '#ffd700' }}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div style={{
            background: '#222',
            padding: '20px',
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '15px'
          }}>
            <h4 style={{ color: '#FFD700', marginBottom: '10px' }}>Current Balance</h4>
            <p style={{ fontSize: '24px', color: '#FFD700', margin: '0' }}>
              {currentBalance.toFixed(6)} {selectedToken}
            </p>
          </div>

          {contractMode && (
            <div style={{ fontSize: '12px', color: '#ffd700', textAlign: 'center' }}>
              Balance from Web3Pay Smart Contract
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{
          background: '#111',
          border: '1px solid #FFD700',
          padding: '20px',
          borderRadius: '8px'
        }}>
          <h3 style={{ color: '#FFD700', marginBottom: '15px' }}>Account Actions</h3>

          {!actionMode ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              <button
                onClick={() => setActionMode('deposit')}
                disabled={!contractMode}
                style={{
                  background: contractMode ? 'linear-gradient(135deg, #00FF00, #00AA00)' : '#444',
                  color: contractMode ? 'black' : '#666',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '8px',
                  cursor: contractMode ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                Deposit {selectedToken}
              </button>

              <button
                onClick={() => setActionMode('withdraw')}
                disabled={!contractMode}
                style={{
                  background: contractMode ? 'linear-gradient(135deg, #FF6B6B, #FF4444)' : '#444',
                  color: contractMode ? 'black' : '#666',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '8px',
                  cursor: contractMode ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                Withdraw {selectedToken}
              </button>

              <button
                onClick={() => setActionMode('transfer')}
                disabled={!contractMode}
                style={{
                  background: contractMode ? 'linear-gradient(135deg, #FFD700, #FFED4E)' : '#444',
                  color: contractMode ? 'black' : '#666',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '8px',
                  cursor: contractMode ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                Transfer {selectedToken}
              </button>

              {!contractMode && (
                <div style={{
                  fontSize: '12px',
                  color: '#ffd700',
                  textAlign: 'center',
                  marginTop: '10px'
                }}>
                  Contract mode required for token operations
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', color: '#FFD700', marginBottom: '5px' }}>
                  Amount
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  min="0"
                  step="0.000001"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ffd700',
                    borderRadius: '8px',
                    background: loading ? '#222' : '#1a1a2e',
                    color: loading ? '#666' : '#ffd700',
                    fontSize: '14px'
                  }}
                />
              </div>

              {actionMode === 'transfer' && (
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', color: '#FFD700', marginBottom: '5px' }}>
                    Recipient Address/ENS
                  </label>
                  <input
                    type="text"
                    placeholder="0x... or name.eth"
                    value={transferRecipient}
                    onChange={(e) => setTransferRecipient(e.target.value)}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ffd700',
                      borderRadius: '88px',
                      background: loading ? '#222' : '#1a1a2e',
                      color: loading ? '#666' : '#ffd700',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleAction}
                  disabled={loading || !transferAmount || (actionMode === 'transfer' && !transferRecipient)}
                  style={{
                    flex: 1,
                    background: loading ? '#444' : 'linear-gradient(135deg, #FFD700, #FFED4E)',
                    color: loading ? '#666' : 'black',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}
                >
                  {loading ? 'Processing...' : `Confirm ${actionMode}`}
                </button>

                <button
                  onClick={() => {
                    setActionMode('');
                    setTransferAmount('');
                    setTransferRecipient('');
                    setError(null);
                  }}
                  disabled={loading}
                  style={{
                    background: 'transparent',
                    color: '#FFD700',
                    border: '1px solid #FFD700',
                    padding: '12px',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    fontSize: '16px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Transactions */}
      <div style={{
        background: '#111',
        border: '1px solid #FFD700',
        padding: '20px',
        borderRadius: '8px'
      }}>
        <h3 style={{ color: '#FFD700', marginBottom: '15px' }}>Recent Transactions</h3>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: '#222',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <thead>
            <tr style={{ background: '#333' }}>
              <th style={{
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left'
              }}>
                Date
              </th>
              <th style={{
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left'
              }}>
                Description
              </th>
              <th style={{
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'right'
              }}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx, index) => (
              <tr key={index}>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: '#ccc'
                }}>
                  {tx.date}
                </td>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: '#ccc'
                }}>
                  {tx.desc}
                </td>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: tx.type === 'credit' ? '#00FF00' : '#FF6B6B',
                  textAlign: 'right',
                  fontWeight: 'bold'
                }}>
                  {tx.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BankCurrent;
